import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, generateStrongPassword } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopBar from "@/components/TopBar";
import SectionView from "@/components/SectionView";
import { SECTIONS, STAGES } from "@/config/sections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Info, Users2, Plus, Trash2, Save, Trash, MapPin, ChevronRight, FileDown, RefreshCw, KeyRound,
} from "lucide-react";

import { API } from "@/lib/api";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [active, setActive] = useState("info");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}`)
      .then(({ data }) => setProject(data))
      .catch(() => setNotFound(true));
  }, [id]);

  const exportPdf = () => {
    const token = localStorage.getItem("ordine3_token");
    window.open(`${API}/projects/${id}/export?auth=${token}`, "_blank");
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="max-w-md mx-auto py-32 text-center">
          <p className="font-heading text-2xl font-light">Progetto non disponibile</p>
          <p className="text-sm text-muted-foreground mt-2">Non hai accesso a questo progetto o non esiste.</p>
          <Button onClick={() => navigate("/dashboard")} className="rounded-none mt-6 bg-foreground text-background">
            Torna ai progetti
          </Button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grain">
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <button onClick={() => navigate("/dashboard")} data-testid="back-button"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Tutti i progetti
        </button>

        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">Progetto</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mt-2">{project.client_name}</h1>
            <p className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
              <MapPin className="h-4 w-4" />
              {[project.property_data?.address, project.property_data?.city].filter(Boolean).join(", ") || "Immobile da definire"}
            </p>
          </div>
          <div className="md:w-72 space-y-3">
            <div>
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span className="uppercase tracking-[0.12em] text-muted-foreground">{project.status || "Progettazione"}</span>
                <span className="font-medium">{project.progress ?? 0}%</span>
              </div>
              <div className="h-1.5 w-full bg-secondary" data-testid="project-progress-bar">
                <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${project.progress ?? 0}%` }} />
              </div>
            </div>
            <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-button"
              className="rounded-none w-full gap-2">
              <FileDown className="h-4 w-4" /> Esporta PDF
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-0 border border-border bg-white">
          {/* Sidebar */}
          <aside className="border-b lg:border-b-0 lg:border-r border-border">
            <nav className="flex lg:flex-col overflow-x-auto">
              <SideItem id="info" active={active} setActive={setActive} icon={Info} label="Dati progetto" />
              {isAdmin && <SideItem id="membri" active={active} setActive={setActive} icon={Users2} label="Collaboratori" />}
              <div className="hidden lg:block px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/60">
                Fasi progetto
              </div>
              {SECTIONS.map((s) => (
                <SideItem key={s.key} id={s.key} active={active} setActive={setActive} label={s.label} />
              ))}
            </nav>
          </aside>

          {/* Content */}
          <section className="p-6 sm:p-10 min-h-[60vh]">
            {active === "info" && <InfoTab project={project} setProject={setProject} isAdmin={isAdmin} onDeleted={() => navigate("/dashboard")} />}
            {active === "membri" && isAdmin && <MembersTab projectId={id} project={project} />}
            {SECTIONS.find((s) => s.key === active) && (
              <SectionView projectId={id} section={SECTIONS.find((s) => s.key === active)} />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function SideItem({ id, active, setActive, icon: Icon, label }) {
  const on = active === id;
  return (
    <button
      data-testid={`section-nav-${id}`}
      onClick={() => setActive(id)}
      className={`flex items-center justify-between gap-2 px-4 py-3 text-sm text-left border-b border-border lg:border-b-0 whitespace-nowrap transition-colors
        ${on ? "bg-foreground text-background font-medium" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
    >
      <span className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" />} {label}
      </span>
      {on && <ChevronRight className="h-4 w-4 hidden lg:block" />}
    </button>
  );
}

function InfoTab({ project, setProject, isAdmin, onDeleted }) {
  const pd = project.property_data || {};
  const [form, setForm] = useState({
    client_name: project.client_name,
    address: pd.address || "", city: pd.city || "", type: pd.type || "",
    size: pd.size || "", year: pd.year || "", notes: pd.notes || "",
    status: project.status || "Progettazione",
    progress: project.progress ?? 20,
    password: project.password || "",
  });
  const [saving, setSaving] = useState(false);

  const onStageChange = (value) => {
    const stage = STAGES.find((s) => s.value === value);
    setForm({ ...form, status: value, progress: stage ? stage.progress : form.progress });
  };

  const save = async () => {
    if (!form.password.trim()) {
      toast.error("Imposta una password per il progetto");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${project.id}`, {
        client_name: form.client_name,
        status: form.status, progress: form.progress,
        password: form.password,
        property_data: {
          address: form.address, city: form.city, type: form.type,
          size: form.size, year: form.year, notes: form.notes,
        },
      });
      setProject(data);
      toast.success("Dati aggiornati");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Progetto eliminato");
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const disabled = !isAdmin;

  return (
    <div className="space-y-8 fade-up max-w-2xl">
      <div>
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">Anagrafica</p>
        <h2 className="font-heading text-3xl font-light tracking-tight mt-2">Dati progetto</h2>
      </div>

      <div className="space-y-4">
        <F label="Nome e cognome cliente">
          <Input disabled={disabled} value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
            className="rounded-none focus-visible:ring-foreground" data-testid="info-client-name" />
        </F>
        <F label="Stato / Fase cantiere">
          <div className="flex items-center gap-4">
            <Select value={form.status} onValueChange={onStageChange} disabled={disabled}>
              <SelectTrigger data-testid="project-stage-select" className="rounded-none focus:ring-foreground max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.value} · {s.progress}%</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1">
              <div className="h-1.5 w-full bg-secondary">
                <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${form.progress}%` }} />
              </div>
            </div>
          </div>
        </F>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Indirizzo immobile"><Input disabled={disabled} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none focus-visible:ring-foreground" /></F>
          <F label="Città"><Input disabled={disabled} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none focus-visible:ring-foreground" /></F>
          <F label="Tipologia"><Input disabled={disabled} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-none focus-visible:ring-foreground" /></F>
          <F label="Superficie (mq)"><Input disabled={disabled} value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="rounded-none focus-visible:ring-foreground" /></F>
          <F label="Anno di costruzione"><Input disabled={disabled} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="rounded-none focus-visible:ring-foreground" /></F>
        </div>
        <F label="Note immobile">
          <Textarea disabled={disabled} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className="rounded-none focus-visible:ring-foreground" />
        </F>
        {isAdmin && (
          <F label="Password del progetto">
            <div className="flex gap-2">
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password condivisa con chi lavora al progetto"
                className="rounded-none focus-visible:ring-foreground" data-testid="info-password" />
              <Button type="button" variant="outline" data-testid="info-generate-password"
                onClick={() => setForm({ ...form, password: generateStrongPassword() })}
                className="rounded-none gap-2 shrink-0">
                <RefreshCw className="h-4 w-4" /> Genera
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Chi possiede questa password può sbloccare e aprire il progetto dalla home. Se la cambi, chi aveva già sbloccato il progetto dovrà inserire quella nuova.</p>
          </F>
        )}
      </div>

      {isAdmin && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <Button onClick={save} disabled={saving} data-testid="save-info-button"
            className="rounded-none gap-2 bg-foreground text-background hover:bg-foreground/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva dati
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="delete-project-button"
                className="rounded-none gap-2 text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground">
                <Trash className="h-4 w-4" /> Elimina progetto
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none">
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare il progetto?</AlertDialogTitle>
                <AlertDialogDescription>
                  Verranno rimossi dati, sezioni, collaboratori e documenti associati. L'operazione non è reversibile.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none">Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={del} data-testid="confirm-delete-project"
                  className="rounded-none bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

function MembersTab({ projectId, project }) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role_label: "" });

  const load = () => {
    api.get(`/projects/${projectId}/members`).then(({ data }) => setMembers(data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [projectId]);

  const createMember = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Inserisci nome, email e password del membro");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/members/create`, {
        name: form.name, email: form.email, password: form.password,
        role_label: form.role_label, notify: true,
      });
      toast.success(data?.notified
        ? "Account creato · email di avviso inviata al membro"
        : "Account membro creato");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role_label: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/projects/${projectId}/members/${uid}`);
      toast.success("Rimosso: non può più aprire questo progetto");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8 fade-up max-w-2xl">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">Accessi progetto</p>
          <h2 className="font-heading text-3xl font-light tracking-tight mt-2">Collaboratori</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Crea un account per ogni membro. Accederà dalla pagina "Accedi" con la sua email e password,
            poi aprirà questo progetto inserendo la password del progetto.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-member-button" className="rounded-none gap-2 shrink-0 bg-foreground text-background hover:bg-foreground/90">
              <Plus className="h-4 w-4" /> Crea account
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-light text-2xl">Nuovo account membro</DialogTitle>
              <DialogDescription>
                Queste sono le credenziali di accesso al gestionale. Riceverà un'email di avviso.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <F label="Nome del membro">
                <Input data-testid="member-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Luca Bianchi"
                  className="rounded-none focus-visible:ring-foreground" />
              </F>
              <F label="Email di accesso">
                <Input data-testid="member-email-input" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="membro@email.it"
                  className="rounded-none focus-visible:ring-foreground" />
              </F>
              <F label="Password di accesso">
                <div className="flex gap-2">
                  <Input data-testid="member-password-input" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="rounded-none focus-visible:ring-foreground" />
                  <Button type="button" variant="outline" data-testid="member-generate-password"
                    onClick={() => setForm({ ...form, password: generateStrongPassword(12) })}
                    className="rounded-none gap-2 shrink-0"><RefreshCw className="h-4 w-4" /> Genera</Button>
                </div>
              </F>
              <F label="Ruolo nel progetto (es. Cliente, Geometra, Falegname)">
                <Input value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })}
                  placeholder="Falegname" className="rounded-none focus-visible:ring-foreground" data-testid="member-role-input" />
              </F>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={createMember} disabled={saving} data-testid="member-save-button"
                className="rounded-none bg-foreground text-background hover:bg-foreground/90">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-secondary/40 p-4 flex items-start gap-3" data-testid="project-password-reminder">
        <KeyRound className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="text-sm">
          <span className="text-muted-foreground">Password di questo progetto da condividere con i membri: </span>
          <span className="font-mono font-medium text-foreground">{project?.password || "— (impostala nei Dati progetto)"}</span>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
          Nessun account membro creato. Clicca "Crea account" per aggiungere chi lavora al progetto.
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {members.map((m) => (
            <div key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium">{m.name} {m.role_label && <span className="text-muted-foreground font-normal">· {m.role_label}</span>}</p>
                <p className="text-xs text-muted-foreground">{m.email}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(m.user_id)} data-testid={`remove-member-${m.user_id}`}
                className="rounded-none h-8 w-8 text-muted-foreground hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold tracking-[0.08em] uppercase text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
